'use client'

import React, { createContext, useContext, useMemo, useState, useEffect, useCallback } from 'react'
import type { ThemeConfig, ThemeTranslations, ThemeColors } from './types'
import { mergeTheme, mergeTranslations, generateCssVariables } from './utils'
import { defaultTheme, defaultTranslations } from './default-theme'

interface ThemeContextValue {
  theme: ThemeConfig
  translations: ThemeTranslations
  darkMode: boolean
  toggleDarkMode: () => void
  setDarkMode: (enabled: boolean) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

export interface ThemeProviderProps {
  children: React.ReactNode
  theme?: Partial<ThemeConfig>
  translations?: Partial<ThemeTranslations>
  /**
   * If true, injects CSS variables into the document root
   * Useful for runtime theming with CSS variables
   */
  injectCssVariables?: boolean
  /**
   * Enable dark mode support
   * @default true
   */
  enableDarkMode?: boolean
  /**
   * Initial dark mode state (only used on first render if no localStorage value)
   * @default false
   */
  defaultDarkMode?: boolean
}

// Auto-generate dark mode colors from light colors
function generateDarkColors(lightColors: ThemeColors): ThemeColors {
  return {
    primary: lightColors.primary,
    primaryDark: lightColors.primaryLight || lightColors.primary,
    primaryLight: lightColors.primaryDark || lightColors.primary,
    secondary: lightColors.secondary,
    secondaryDark: lightColors.secondaryLight || lightColors.secondary,
    secondaryLight: lightColors.secondaryDark || lightColors.secondary,
    success: lightColors.success || '#10b981',
    warning: lightColors.warning || '#f59e0b',
    error: lightColors.error || '#ef4444',
    info: lightColors.info || '#3b82f6',
    background: '#0f172a', // slate-900
    surface: '#1e293b', // slate-800
    text: '#f1f5f9', // slate-100
    textSecondary: '#cbd5e1', // slate-300
    border: '#334155', // slate-700
    divider: '#475569', // slate-600
  }
}

/**
 * Theme Provider Component
 * Wraps your application and provides theme context to all components
 *
 * @example
 * ```tsx
 * import { ThemeProvider } from '@next-wordpress/ui/theme'
 *
 * const myTheme = {
 *   colors: {
 *     primary: '#00529F',
 *     secondary: '#FEBE10',
 *   }
 * }
 *
 * export default function App() {
 *   return (
 *     <ThemeProvider theme={myTheme} enableDarkMode>
 *       <YourApp />
 *     </ThemeProvider>
 *   )
 * }
 * ```
 */
export function ThemeProvider({
  children,
  theme: userTheme,
  translations: userTranslations,
  injectCssVariables = false,
  enableDarkMode = true,
  defaultDarkMode = false,
}: ThemeProviderProps) {
  // Dark mode state with localStorage persistence
  const [darkMode, setDarkModeState] = useState(defaultDarkMode)
  const [mounted, setMounted] = useState(false)

  // Initialize dark mode from localStorage on mount
  useEffect(() => {
    setMounted(true)
    if (enableDarkMode && typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme-dark-mode')
      if (stored !== null) {
        setDarkModeState(stored === 'true')
      }
    }
  }, [enableDarkMode])

  // Persist dark mode to localStorage
  useEffect(() => {
    if (mounted && enableDarkMode && typeof window !== 'undefined') {
      localStorage.setItem('theme-dark-mode', String(darkMode))

      // Update document class for Tailwind dark mode
      if (darkMode) {
        document.documentElement.classList.add('dark')
      } else {
        document.documentElement.classList.remove('dark')
      }
    }
  }, [darkMode, mounted, enableDarkMode])

  const baseTheme = useMemo(
    () => (userTheme ? mergeTheme(userTheme) : defaultTheme),
    [userTheme]
  )

  // Apply dark mode colors if enabled
  const theme = useMemo(() => {
    if (!enableDarkMode || !darkMode) {
      return baseTheme
    }

    const darkColors = baseTheme.darkColors || generateDarkColors(baseTheme.colors)

    return {
      ...baseTheme,
      colors: darkColors,
    }
  }, [baseTheme, darkMode, enableDarkMode])

  const translations = useMemo(
    () =>
      userTranslations
        ? mergeTranslations(userTranslations)
        : defaultTranslations,
    [userTranslations]
  )

  const cssVariables = useMemo(
    () => (injectCssVariables ? generateCssVariables(theme) : {}),
    [theme, injectCssVariables]
  )

  const toggleDarkMode = useCallback(() => {
    if (enableDarkMode) {
      setDarkModeState((prev) => !prev)
    }
  }, [enableDarkMode])

  const setDarkMode = useCallback((enabled: boolean) => {
    if (enableDarkMode) {
      setDarkModeState(enabled)
    }
  }, [enableDarkMode])

  const value = useMemo(
    () => ({
      theme,
      translations,
      darkMode: enableDarkMode ? darkMode : false,
      toggleDarkMode,
      setDarkMode,
    }),
    [theme, translations, darkMode, enableDarkMode, toggleDarkMode, setDarkMode]
  )

  return (
    <ThemeContext.Provider value={value}>
      {injectCssVariables && Object.keys(cssVariables).length > 0 && (
        <style
          dangerouslySetInnerHTML={{
            __html: `:root { ${Object.entries(cssVariables)
              .map(([key, value]) => `${key}: ${value};`)
              .join(' ')} }`,
          }}
        />
      )}
      {children}
    </ThemeContext.Provider>
  )
}

/**
 * Hook to access theme context
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { theme, translations } = useTheme()
 *
 *   return (
 *     <div style={{ color: theme.colors.primary }}>
 *       {translations.common.loading}
 *     </div>
 *   )
 * }
 * ```
 */
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }

  return context
}
